import React, { useEffect, useRef, useState } from 'react';

const RECEIPT_COUNT_URL =
  'https://gettotalreceiptcount-yv2d6tj3aq-uc.a.run.app';
const RECEIPT_COUNT_CACHE_KEY = 'kachng-counter';

function getInitialReceiptCount() {
  try {
    const cached = Number(localStorage.getItem(RECEIPT_COUNT_CACHE_KEY));
    return Number.isFinite(cached) && cached >= 0 ? cached : 0;
  } catch {
    return 0;
  }
}

/**
 * Trust section (#trust) — "On the record."
 * A bordered strip of five trust/proof cells. The receipts-processed cell
 * loads the live total and counts up when it enters the viewport.
 */

function Count({ value, style }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const format = (number) => Math.round(number).toLocaleString('en-US');

    if (prefersReduced) {
      el.textContent = format(value);
      return undefined;
    }

    el.textContent = format(0);
    let animationFrame = 0;
    let started = false;

    const run = () => {
      const duration = 1100;
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        el.textContent = format(value * eased);

        if (progress < 1) {
          animationFrame = requestAnimationFrame(tick);
        } else {
          el.textContent = format(value);
        }
      };

      animationFrame = requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return () => cancelAnimationFrame(animationFrame);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            run();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(el);

    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [value]);

  return (
    <span ref={ref} style={style}>
      {value.toLocaleString('en-US')}
    </span>
  );
}

const cellStyle = {
  flex: '1 1 150px',
  minWidth: 0,
  boxSizing: 'border-box',
  padding: 'clamp(24px,2.8vw,38px) 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
  borderTop: '1px solid var(--border-hairline)',
  borderLeft: '1px solid var(--border-hairline)',
};

const bigStyle = {
  fontSize: 'clamp(18px,1.9vw,28px)',
  fontWeight: 800,
  letterSpacing: '-0.02em',
  whiteSpace: 'nowrap',
};

const subStyle = {
  fontSize: '13.5px',
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

const CELLS = [
  {
    big: 'Privacy by design',
    sub: 'Own your data so it works for you, not against you',
  },
  {
    big: 'No bank',
    sub: 'Works without connecting your bank account',
  },
  {
    liveCount: true,
    sub: 'receipts processed',
  },
  {
    big: 'Keep your cards',
    sub: 'works alongside the rewards cards you already use',
  },
  {
    big: '1 tree',
    sub: 'funded for every 100 eligible digital receipts processed',
  },
];

export default function Trust() {
  const [receiptCount, setReceiptCount] = useState(getInitialReceiptCount);

  useEffect(() => {
    const controller = new AbortController();

    const fetchReceiptCount = async () => {
      try {
        const response = await fetch(RECEIPT_COUNT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: {} }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Receipt count request failed: ${response.status}`);
        }

        const result = await response.json();
        const liveCount = result?.result?.count;

        if (typeof liveCount === 'number' && Number.isFinite(liveCount)) {
          setReceiptCount(liveCount);

          try {
            localStorage.setItem(RECEIPT_COUNT_CACHE_KEY, String(liveCount));
          } catch {
            // The live count still works when browser storage is unavailable.
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching receipt count:', error);
        }
      }
    };

    fetchReceiptCount();

    return () => controller.abort();
  }, []);

  return (
    <section id="trust">
      <div className="mobile-section-inner"
        style={{
          maxWidth: 'var(--max-width)',
          margin: '0 auto',
          boxSizing: 'border-box',
          padding: 'clamp(56px,7vw,88px) clamp(20px,5vw,56px) 0',
        }}
      >
        <h2
          data-rv="up"
          style={{
            margin: '0 0 clamp(20px,2.5vw,32px)',
            fontSize: 'clamp(34px,4.4vw,56px)',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.02,
            textTransform: 'uppercase',
          }}
        >
          On the record.
        </h2>

        <div style={{ overflow: 'hidden' }}>
          <div className="trust-grid"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              margin: '-1px 0 0 -1px',
            }}
          >
            {CELLS.map((cell, index) => (
              <div className="trust-cell" key={index} data-rv="up" style={cellStyle}>
                {cell.liveCount ? (
                  <Count
                    value={receiptCount}
                    style={{
                      ...bigStyle,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  />
                ) : (
                  <div style={bigStyle}>{cell.big}</div>
                )}
                <div style={subStyle}>{cell.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
